// ModalTabs.js - Updated sections
import React, { useState, useRef, useEffect } from 'react';

// Word-specific tabs
import DetailsTab from './WordModalTabs/DetailsTab';
import ExamplesTab from './WordModalTabs/ExamplesTab';
import SeeAlsoTab from './WordModalTabs/SeeAlsoTab';

// Skeleton components
import ExamplesTabSkeleton from './WordModalTabs/skeletons/ExamplesTabSkeleton';
import SeeAlsoTabSkeleton from './WordModalTabs/skeletons/SeeAlsoTabSkeleton';

// Sentence-specific tabs
import SentenceDetailsTab from './SentenceModalTabs/SentenceDetailsTab';
import CulturalContextTab from './SentenceModalTabs/CulturalContextTab';
import PolitenessTab from './SentenceModalTabs/PolitenessTab';

// Import the data loading functions
import { getExampleSentenceIdsForWord, getMultipleWordDetails } from '../../../firebase/words';
import { refetchSingleItem } from '../../../firebase/course';
import { useMetadata } from '../../../contexts/MetadataProvider';

const TabButton = ({ children, isActive, onClick, disabled }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    className={`flex-1 px-4 py-3 text-sm font-semibold rounded-t-lg transition-all duration-200
      ${
        isActive
          ? 'bg-primary/10 text-primary border-b-2 border-primary'
          : 'text-content-secondary hover:bg-background-primary hover:text-content-primary'
      }
      ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
    `}
  >
    {children}
  </button>
);

const ModalTabs = ({ item, activeTab, setActiveTab, onItemClick, userId }) => {
  const { learnedWordIds } = useMetadata();

  // Function to refetch single item when it's updated
  const refetchTabData = async (updatedItemId) => {
    // Only refetch if the current tab has data and the updated item might be in it
    if (activeTab === 'examples' && tabData.examples?.loadedSentences) {
      const itemIndex = tabData.examples.loadedSentences.findIndex(example => example.id === updatedItemId);
      if (itemIndex !== -1) {
        try {
          const updatedItem = await refetchSingleItem(updatedItemId, userId, 'sentences');
          setTabData(prev => ({
            ...prev,
            examples: {
              ...prev.examples,
              loadedSentences: prev.examples.loadedSentences.map(example =>
                example.id === updatedItemId ? updatedItem : example
              )
            }
          }));
        } catch (error) {
          console.error('Failed to refetch example item:', error);
        }
      }
    } else if (activeTab === 'seeAlso' && tabData.seeAlso) {
      // Check if the updated item is in seeAlso
      const itemIndex = tabData.seeAlso.findIndex(word => word.id === updatedItemId);
      if (itemIndex !== -1) {
        try {
          // Refetch just this single item
          const updatedItem = await refetchSingleItem(updatedItemId, userId, 'words', learnedWordIds);
          setTabData(prev => ({
            ...prev,
            seeAlso: prev.seeAlso.map((word, index) => 
              index === itemIndex ? updatedItem : word
            )
          }));
        } catch (error) {
          console.error('Failed to refetch seeAlso item:', error);
        }
      }
    }
  };
  const [isLoading, setIsLoading] = useState(false);
  const [tabData, setTabData] = useState({
    examples: null,
    seeAlso: null,
  });
  const [loadedTabs, setLoadedTabs] = useState(new Set(['details']));
  const [containerHeight, setContainerHeight] = useState('auto');

  const contentRef = useRef(null);
  const isWord = !!item.word;

  // Measure content height
  const measureHeight = () => {
    if (contentRef.current) {
      const height = contentRef.current.scrollHeight;
      return height;
    }
    return 200; // fallback height
  };

  // Update height when content changes
  useEffect(() => {
    if (!isLoading && contentRef.current) {
      const height = measureHeight();
      setContainerHeight(`${height}px`);

      // Reset to auto after transition completes
      const timer = setTimeout(() => {
        setContainerHeight('auto');
      }, 300); // Match the transition duration

      return () => clearTimeout(timer);
    }
  }, [activeTab, isLoading, tabData]);

  // Load initial tab data when component mounts with non-default tab
  useEffect(() => {
    const loadInitialTabData = async () => {
      if (activeTab !== 'details' && tabNeedsLoading(activeTab)) {
        setIsLoading(true);
        try {
          await loadTabData(activeTab);
          setLoadedTabs((prev) => new Set([...prev, activeTab]));
        } catch (error) {
          console.error('Error loading initial tab data:', error);
        } finally {
          setIsLoading(false);
        }
      }
    };

    loadInitialTabData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.id, activeTab]); // Only run when item or activeTab changes

  // Data loading functions - only for word tabs that need data fetching
  const loadTabData = async (tabName) => {
    if (isWord) {
      switch (tabName) {
        case 'examples': {
          const sentenceIds = await getExampleSentenceIdsForWord(item.id);
          setTabData((prev) => ({ ...prev, examples: { sentenceIds, loadedSentences: [] } }));
          break;
        }
        case 'seeAlso': {
          const allIds = [...(item.see_also || []), ...(item.antonyms || [])];
          if (allIds.length > 0) {
            const relatedWords = await getMultipleWordDetails(allIds, userId, learnedWordIds);
            setTabData((prev) => ({ ...prev, seeAlso: relatedWords }));
          } else {
            setTabData((prev) => ({ ...prev, seeAlso: [] }));
          }
          break;
        }
      }
    }
    // For sentence tabs (context, politeness), no data loading needed
  };

  // Check if a tab needs data loading
  const tabNeedsLoading = (tabName) => {
    if (!isWord) {
      // Sentence tabs never need loading since data is already in item
      return false;
    }

    // Word tabs that need loading
    return (tabName === 'examples' || tabName === 'seeAlso') && !loadedTabs.has(tabName);
  };

  // Enhanced tab change handler with skeleton loading
  const handleTabChange = async (newTab) => {
    if (newTab === activeTab) return;

    // Check if we need to load data for this tab
    const needsLoading = tabNeedsLoading(newTab);

    if (needsLoading) {
      // Step 1: Switch to new tab immediately to show skeleton
      setActiveTab(newTab);
      setIsLoading(true);

      // Step 2: Lock current height to prevent jumping
      const currentHeight = measureHeight();
      setContainerHeight(`${currentHeight}px`);

      try {
        await loadTabData(newTab);
        setLoadedTabs((prev) => new Set([...prev, newTab]));

        // Small delay to ensure data is loaded
        await new Promise((resolve) => setTimeout(resolve, 50));
      } catch (error) {
        console.error('Error during tab change:', error);
      } finally {
        setIsLoading(false);
        
        // Step 3: Measure new content height after loading
        setTimeout(() => {
          if (contentRef.current) {
            const newHeight = measureHeight();
            setContainerHeight(`${newHeight}px`);
          }
        }, 50);
      }
    } else {
      // Step 1: Lock current height
      const currentHeight = measureHeight();
      setContainerHeight(`${currentHeight}px`);

      // Step 2: Switch to new tab
      setActiveTab(newTab);

      // Step 3: Measure new content height after a brief delay
      setTimeout(() => {
        if (contentRef.current) {
          const newHeight = measureHeight();
          setContainerHeight(`${newHeight}px`);
        }
      }, 50);
    }
  };

  const renderContent = () => {
    if (isWord) {
      switch (activeTab) {
        case 'details':
          return <DetailsTab senses={item.senses} />;
        case 'examples':
          if (isLoading) {
            return <ExamplesTabSkeleton />;
          }
          return <ExamplesTab
            exampleData={tabData.examples || { sentenceIds: [], loadedSentences: [] }}
            userId={userId}
            onItemClick={(item) => onItemClick(item, refetchTabData)}
            onSentencesChange={(newSentences) => setTabData(prev => ({
              ...prev,
              examples: { ...prev.examples, loadedSentences: newSentences }
            }))}
          />;
        case 'seeAlso':
          if (isLoading) {
            return <SeeAlsoTabSkeleton />;
          }
          return (
            <SeeAlsoTab
              relatedWords={tabData.seeAlso || []}
              seeAlsoIds={item.see_also || []}
              antonymIds={item.antonyms || []}
              onItemClick={(item) => onItemClick(item, refetchTabData)}
              learnedIds={learnedWordIds}
            />
          );
        default:
          return null;
      }
    } else {
      switch (activeTab) {
        case 'details':
          return <SentenceDetailsTab sentence={item} />;
        case 'context':
          return <CulturalContextTab context={item.cultural_context} />;
        case 'politeness':
          return <PolitenessTab politeness={item.politeness} />;
        default:
          return null;
      }
    }
  };

  return (
    <>
      {/* Tab navigation */}
      <div className="border-b border-border sticky top-0 bg-background-secondary z-10">
        <nav className="flex px-4 md:px-6">
          {isWord ? (
            <>
              <TabButton isActive={activeTab === 'details'} onClick={() => handleTabChange('details')} disabled={isLoading}>
                Meanings
              </TabButton>
              <TabButton isActive={activeTab === 'examples'} onClick={() => handleTabChange('examples')} disabled={isLoading}>
                Examples
              </TabButton>
              {(item.see_also?.length > 0 || item.antonyms?.length > 0) && (
                <TabButton isActive={activeTab === 'seeAlso'} onClick={() => handleTabChange('seeAlso')} disabled={isLoading}>
                  Related
                </TabButton>
              )}
            </>
          ) : (
            <>
              <TabButton isActive={activeTab === 'details'} onClick={() => handleTabChange('details')} disabled={isLoading}>
                Details
              </TabButton>
              {item.cultural_context && (
                <TabButton isActive={activeTab === 'context'} onClick={() => handleTabChange('context')} disabled={isLoading}>
                  Context
                </TabButton>
              )}
              {item.politeness && (
                <TabButton isActive={activeTab === 'politeness'} onClick={() => handleTabChange('politeness')} disabled={isLoading}>
                  Politeness
                </TabButton>
              )}
            </>
          )}
        </nav>
      </div>

      {/* Content container with smooth height transitions */}
      <div
        className="relative overflow-hidden transition-all duration-300 ease-in-out"
        style={{
          height: containerHeight,
          minHeight: '200px',
        }}
      >
        {/* Content - no loading overlay needed anymore */}
        <div ref={contentRef}>
          {renderContent()}
        </div>
      </div>
    </>
  );
};

export default ModalTabs;